<template>
    <div id="app" :class="[currentPage, themeClass]">
        <md-app>
            <md-app-toolbar class="md-primary thin">
                <div class="md-toolbar-section-start">
                    <a href="/">
                        <div v-if="currentPage=='home-page'"></div>
                        <img  v-else class="logo-toolbar" :src="banner" alt="EarthBlox Banner" />
                    </a>
                </div>

                <a tabindex="0" v-on:click="skipToContent()" v-on:keyup.enter="skipToContent()" class="skipLink">Skip to main content</a>

                <div class="md-toolbar-section-end">
                    <h3 class="md-title" v-if="user.displayName">Welcome {{ user.displayName.split(" ")[0] }}</h3>
                    <md-button v-if="$router.currentRoute.name === 'Home'" class="md-raised" @click="signin">Log in</md-button>
                    <md-menu md-direction="bottom-start">
                        <md-button md-menu-trigger class="md-icon-button">
                            <img v-if="user.photoURL" :src="user.photoURL" alt="Avatar" />
                            <md-icon v-else>person</md-icon>
                            <md-tooltip md-direction="bottom">Your account</md-tooltip>
                        </md-button>

                        <md-menu-content>
                            <md-menu-item to="/myaccount" v-if="user.uid">My Account</md-menu-item>
                            <md-menu-item @click="signout" v-if="user.uid">Sign Out</md-menu-item>
                            <md-menu-item @click="signin" v-else>Sign In</md-menu-item>
                            <md-menu-item>S0.9.6<!-- insert version number here --></md-menu-item>
                        </md-menu-content>
                    </md-menu>

                    <md-menu md-direction="bottom-start">
                        <md-button md-menu-trigger class="md-icon-button">
                            <md-icon>more_vert</md-icon>
                            <md-tooltip md-direction="bottom">Menu</md-tooltip>
                        </md-button>

                        <md-menu-content>
                            <md-menu-item v-if="getOrgType === 'mooc'" href="https://forms.gle/BHZgFdfTmongeRhi9" target="_blank">
                                <span>Upgrade to Earth Blox Enterprise</span>
                            </md-menu-item>

                            <md-menu-item to="/workflow" v-if="user.uid && user.orgId">Workspace</md-menu-item>
                            <md-menu-item to="/admin/users" v-if="isAdmin">Admin Panel</md-menu-item>
                            <!-- <md-menu-item v-if="user.uid && user.orgId" to="/tutorials">Tutorials</md-menu-item> -->
                            <!-- <md-menu-item to="/suggestion">Make a suggestion</md-menu-item> -->
                            <md-menu-item :href="helpGuide" target="_blank">
                                <span>Getting started guide</span>
                                <md-icon>open_in_new</md-icon>
                            </md-menu-item>
                            <md-menu-item href="https://www.earthblox.io/privacy" target="_blank">
                                <span>Privacy policy</span>
                                <md-icon>open_in_new</md-icon>
                            </md-menu-item>
                        </md-menu-content>
                    </md-menu>

                </div>
            </md-app-toolbar>

            <md-app-content tabindex="-1" id="main" ref="mainContent" v-bind:class="appContentClass">
                <router-view />
            </md-app-content>
        </md-app>
    </div>
</template>

<script>
/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
*/
// @ is an alias to /src

import { earthbloxCollection } from "@/firebase.js";
import { AuthService } from "./services/auth.service";
import { ConfigService } from '@/services/config.service';
import { CONFIG_TYPE } from "@/config/constants.js";

export default {
    name: "App",
    data: () => ({
        banner: null,
        user: null,
        showNavigation: false,
        currentPage: null,
        helpGuide: null,
        orgConfig: null
    }),
    created() {

        let self = this;

        earthbloxCollection.doc("homepage").onSnapshot(function(doc) {
            if (doc.exists) {
                self.banner = doc.data().banner;
                self.helpGuide = doc.data().helpGuide;
            } else {
                self.helpGuide = "https://blox.earth/";
            }
        });

        this.subscription = AuthService.loggedUser$.subscribe(function(user) {
            this.user = user;
            ConfigService.getOrgConfig(this.user.orgId).then(function(config){
                this.orgConfig = config;
            }.bind(this));
        }.bind(this));

        console.info("Vue app created")
    },
    beforeDestroy() {
        // unsubscribe to ensure no memory leaks
        this.subscription.unsubscribe();
    },
    methods: {
        skipToContent() {
            console.log('skip links clicked')
            this.$refs.mainContent.$el.focus();
        },
        signin() {
            this.$router.push({ name: "SignIn" });
        },
        signout() {
            AuthService.signOut();
        }
    },
    computed: {
        isAdmin() {
            return (
                this.user.roles &&
                (this.user.roles.includes("admin") ||
                this.user.roles.includes("superadmin"))
            );
        },
        activeRoute() {
            return this.$router.currentRoute.name;
        },
        appContentClass(){
            if(this.currentPage === "workflow-page") {
                return "md-primary";
            } else {
                return "";
            }
        },
        orgCustomisations() {
            return this.orgConfig ? this.orgConfig.getConfig(CONFIG_TYPE.CUSTOMISATION) : undefined;
        },
        themeClass() {
            if (this.orgCustomisations) {
                return this.orgCustomisations['client.theme'] ? this.orgCustomisations['client.theme'] : '';
            } else {
                return '';
            }
            
        },
        getOrgType() {
            return this.orgConfig ? this.orgConfig.accessType : undefined;
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler(to) {
                this.currentPage = to.name?.toLowerCase() + "-page";
            },
        },
    }
};
</script>


<style lang="scss" scoped>
.md-app,
.md-app-content {
    height: 100%;
}

.logo-toolbar {
    width: 100%;
    height: auto;
    max-height: 50px;
    object-fit: contain;
    object-position: left;
}

@media only screen and (max-width: 800px) {
    .md-content.md-app-content.md-theme-default {
        padding: 8px;
    }
}

.md-toolbar {
    border-bottom: 2px inset #ffffff;
}
</style>

<style>
.md-app .md-app-scroller {
    flex: initial;
    height: calc(100% - 56px);
}

div.md-menu-content {
    z-index: 11;
}

.md-dialog-container > form {
    min-width: 360px;
    display: flex;
    flex-flow: column;
    overflow: hidden;
}

@media (min-width: 601px) {
    .md-app .md-app-scroller {
        height: calc(100% - 48px);
    }
}

@media (min-width: 961px) {
    .md-app .md-app-scroller {
        height: calc(100% - 64px);
    }
}
</style>
